import type { ApiErrorBody, ApiErrorCode } from "@autopilot/shared";

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: ApiErrorCode,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const init: RequestInit = { method, credentials: "same-origin", headers: {} };
  if (body instanceof FormData) {
    init.body = body;
  } else if (body !== undefined) {
    init.body = JSON.stringify(body);
    (init.headers as Record<string, string>)["content-type"] = "application/json";
  }
  const res = await fetch(path.startsWith("/") ? path : `/api/v1/${path}`, init);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? (JSON.parse(text) as unknown) : undefined;
  if (!res.ok) {
    const err = (data as ApiErrorBody | undefined)?.error;
    if (res.status === 403 && err?.details && typeof err.details === "object" && "reason" in err.details && err.details.reason === "mfa_required" && window.location.pathname !== "/mfa") {
      window.location.assign(`/mfa?next=${encodeURIComponent(window.location.pathname + window.location.search)}`);
    }
    throw new ApiRequestError(res.status, err?.code ?? "internal", err?.message ?? `Request failed (${res.status})`, err?.details);
  }
  return data as T;
}

/**
 * JSON API client. Relative paths are resolved under /api/v1 (e.g. `api.get("auth/me")`),
 * absolute paths are used as-is (e.g. `/portal/v1/acme`).
 */
export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body ?? {}),
  put: <T>(path: string, body: unknown) => request<T>("PUT", path, body),
  patch: <T>(path: string, body: unknown) => request<T>("PATCH", path, body),
  del: <T = { ok: true }>(path: string) => request<T>("DELETE", path),
  upload: <T>(path: string, file: File, fields: Record<string, string> = {}) => {
    const form = new FormData();
    form.set("file", file);
    for (const [k, v] of Object.entries(fields)) form.set(k, v);
    return request<T>("POST", path, form);
  },
};

/** Workspace-scoped path helper: ws(wid, "conversations") -> "w/<wid>/conversations". */
export const ws = (wid: string, path = "") => `w/${wid}${path ? `/${path}` : ""}`;

export function errorMessage(err: unknown): string {
  if (err instanceof ApiRequestError) return err.message;
  if (err instanceof Error) return err.message;
  return "Something went wrong";
}
